import { ApiError, authFetch } from "@/lib/api-client";

const HTTP_CONFLICT = 409;

export const ASK_NEXUS_BASE_PATH = "/ask-nexus/conversations";
const ASK_NEXUS_AVAILABILITY_PATH = "/ask-nexus/availability";

export type AskNexusConversationStatus = "running" | "idle" | "limit_reached" | "failed";
export type AskNexusTurnStatus = "running" | "answered" | "stopped" | "timed_out" | "declined" | "limit_reached" | "failed";

/** How a tool call ended, as the server read it from the result itself. */
export type AskNexusToolStatus =
  | "running"
  | "succeeded"
  | "pending"
  | "unavailable"
  | "needs_input"
  | "denied"
  | "failed"
  | "not_finished"
  /** The result carried no status Ask Nexus knows, so it is not taken for data retrieved. */
  | "unrecognized";

export type AskNexusGotBackKind =
  | "data_sets"
  | "brands"
  | "creatives"
  | "opportunities"
  | "personas"
  | "pages"
  | "insight_types"
  | "filters";

export interface AskNexusToolActivity {
  id: string;
  tool: string;
  input: unknown;
  status: AskNexusToolStatus;
  /** What a successful call returned, as counts; null for any other status. */
  gotBack: {
    counts: Array<{ kind: AskNexusGotBackKind; count: number }>;
    startDate: string | null;
    endDate: string | null;
    source: AskNexusDataSource | null;
  } | null;
  /** The start of the raw result, for the "Show raw result" toggle. Null unless the call succeeded. */
  resultPreview: string | null;
}

export type AskNexusDataSource = "owned_accounts" | "public_ad_library";
export type AskNexusBrandRole = "you" | "mainCompetitor" | "benchmark";

/**
 * A limitation the tool states about the data. Known ones arrive as codes the panel words;
 * one the server does not recognize arrives as the tool wrote it, never as an instruction.
 */
export type AskNexusQualityNote =
  | {
      code:
        | "library_counts"
        | "no_competitor_spend"
        | "share_is_volume"
        | "owned_billed"
        | "connected_only"
        | "kpi_per_org"
        | "stale"
        | "library_activity_only";
    }
  | { code: "before_window"; brands: string[] }
  | { code: "tool_note"; text: string };

/** Where a card's values came from: one result of one tool. */
export interface AskNexusCardProvenance {
  tool: string;
  startDate: string | null;
  endDate: string | null;
  presetLabel: string | null;
  source: AskNexusDataSource | "nexus_records";
  pulledAt: string | null;
  /** What the result was scoped to, when the tool says (an industry, a topic). */
  scope: string | null;
  benchmarkBrands: string[] | null;
  isPartial: boolean;
  quality: AskNexusQualityNote[];
}

export interface AskNexusKpiTilesCard {
  card: "kpi_tiles";
  tiles: Array<{
    metric: string;
    label: string;
    value: number;
    previousValue: number;
    format: "currency" | "count" | "percent" | "ratio" | null;
  }>;
  notReturned: string[];
  provenance: AskNexusCardProvenance;
}

export interface AskNexusComparisonTableCard {
  card: "comparison_table";
  basis: "metrics" | "topics";
  field: "assets" | "days" | null;
  columns: Array<{ role: AskNexusBrandRole | null; label: string | null }>;
  rows: Array<{
    label: string;
    cells: Array<{ value: number | null; note: "no_data" | "not_on_page" | "not_returned" | null }>;
  }>;
  notReturned: string[];
  provenance: AskNexusCardProvenance;
}

export interface AskNexusCreativeExample {
  hash: string;
  adName: string;
  brand: string | null;
  /** `organization`: the active organization's name. `competitor`: a brand key, shown through formatBrandName. */
  brandKind: "organization" | "competitor";
  platform: string | null;
  objectType: string;
  mediaType: "image" | "video";
  /** https only, from the tool result. Images show it; a video uses it as the poster. */
  thumbnailUrl: string | null;
  /** https only, from the tool result: the video source and the "Open original" link. */
  creativeUrl: string | null;
  hook: string | null;
  adCopy: string | null;
  runDates: string | null;
  rank: number | null;
  owned: { spend: number | null; ctr: number | null; roas: number | null; ads: number | null; duplications: number | null } | null;
  library: { ads: number | null; assets: number | null; days: number | null; daysPerAsset: number | null } | null;
}

export interface AskNexusCreativeExamplesCard {
  card: "creative_examples";
  side: "my-brand" | "competitor";
  examples: AskNexusCreativeExample[];
  notReturned: string[];
  provenance: AskNexusCardProvenance;
}

export interface AskNexusOpportunitiesCard {
  card: "opportunities";
  items: Array<{
    id: string;
    /** The set it was generated in; its link opens that set or this opportunity. */
    setId: string;
    angle: string;
    personaTitle: string;
    setName: string;
    type: string;
    rank: number;
    painPoint: string | null;
    motivation: string | null;
    drivenBy: "painpoint" | "motivation" | null;
    createdAt: string;
  }>;
  notReturned: string[];
  provenance: AskNexusCardProvenance;
}

/** What a script's or a suggestion's source turned out to be, from the turn's tool results. */
export type AskNexusResolvedSource =
  | { kind: "pack"; tool: string; startDate: string; endDate: string; source: AskNexusDataSource }
  | { kind: "creative_example"; adName: string; brand: string | null; brandKind: "organization" | "competitor" }
  | { kind: "opportunity"; angle: string; setName: string };

export interface AskNexusScriptCard {
  card: "script";
  title: string;
  angle: string;
  persona: string | null;
  platform: string;
  durationSeconds: number;
  scenes: Array<{ label: string; startSeconds: number; endSeconds: number; visual: string; voiceover: string | null; onScreenText: string | null }>;
  cta: string;
  sources: AskNexusResolvedSource[];
  unresolvedSources: number;
  needsConfirmation: string[];
}

export interface AskNexusOpportunitySuggestionCard {
  card: "opportunity_suggestion";
  title: string;
  description: string;
  evidence: Array<{ text: string; source: AskNexusResolvedSource | null; isSourceMissing: boolean }>;
}

export interface AskNexusClarificationOption {
  value: string;
  /** The tool's label; the value itself when it sent none. */
  label: string;
  /** One line under the label; only Nexus catalog options, such as report types, have one. */
  description?: string;
}

export type AskNexusClarificationQuestion =
  | {
      id: string;
      kind: "choice";
      label: string;
      isRequired: boolean;
      isMultiple: boolean;
      /** A brand or platform without a tool label is named the way the dashboard names it. `report`: a Nexus report type. */
      optionKind: "industry" | "brand" | "platform" | "report";
      /** Every option nexus_list_filter_options or a Nexus catalog returned, never one the agent wrote. */
      options: AskNexusClarificationOption[];
      suggested: string[];
      total: number;
      isPartial: boolean;
    }
  | { id: string; kind: "period"; label: string; isRequired: boolean };

export interface AskNexusClarificationCard {
  card: "clarification";
  questions: AskNexusClarificationQuestion[];
}

export type AskNexusPeriodPreset = "month_to_date" | "last_30_days" | "custom";

/** What the person chose; an optional question left empty has no choices. */
export type AskNexusClarificationAnswer =
  | { questionId: string; label: string; choices: AskNexusClarificationOption[] }
  | { questionId: string; label: string; period: { preset: AskNexusPeriodPreset; startDate: string; endDate: string } };

/** A Nexus page the agent pointed to. Nexus resolved its entity from this answer's results; the panel builds the URL. */
export type AskNexusLinkCard =
  | { card: "link"; destination: "opportunity"; opportunityId: string; setId: string; setKind: "persona" | "legacy" }
  | { card: "link"; destination: "opportunity_hub" }
  | { card: "link"; destination: "scripts" }
  | { card: "link"; destination: "personas" }
  | { card: "link"; destination: "dashboard" }
  | { card: "link"; destination: "overview" };

/** Questions the agent suggests asking next, written at the end of the same answer. Sending one is a normal follow-up. */
export interface AskNexusFollowUpsCard {
  card: "follow_ups";
  questions: string[];
}

export type AskNexusReportStatus = "running" | "ready" | "failed" | "cancelled";
export type AskNexusReportFailure = "start_failed" | "timed_out" | "limit_reached" | "no_file" | "agent_failed" | "session_lost";

/** A report as the server shows it: no file, session or vault ids. */
export interface AskNexusReport {
  reportId: string;
  status: AskNexusReportStatus;
  /** The agent's closing lines, once ready. */
  summary: string | null;
  hasPowerPoint: boolean;
  failure: AskNexusReportFailure | null;
}

/**
 * A report the person confirmed in this answer. The server validated the report type and that the platform is
 * connected; the panel starts the report for it once, and shows its progress and the download.
 */
export interface AskNexusReportRequestCard {
  card: "report_request";
  /** Names the request when starting it; null only if the server could not name the turn. */
  requestId: string | null;
  reportType: { value: string; label: string; description: string };
  platform: string;
  /** The report started for this request, if any. */
  report: AskNexusReport | null;
}

export type AskNexusCard =
  | AskNexusReportRequestCard
  | AskNexusKpiTilesCard
  | AskNexusComparisonTableCard
  | AskNexusCreativeExamplesCard
  | AskNexusOpportunitiesCard
  | AskNexusScriptCard
  | AskNexusOpportunitySuggestionCard
  | AskNexusClarificationCard
  | AskNexusLinkCard
  | AskNexusFollowUpsCard;

/** Cards that show tool data with its provenance: not suggestions, not questions. */
export type AskNexusDataCard = Exclude<
  AskNexusCard,
  | AskNexusScriptCard
  | AskNexusOpportunitySuggestionCard
  | AskNexusClarificationCard
  | AskNexusLinkCard
  | AskNexusFollowUpsCard
  | AskNexusReportRequestCard
>;

export type AskNexusAnswerBlock =
  | { type: "markdown"; text: string }
  | { type: "card"; card: AskNexusCard }
  | { type: "card_unavailable"; reason: "invalid" | "not_found" | "no_values" | "too_many" };

export interface AskNexusTurn {
  /** Empty when the question is older than what the server read. */
  message: string;
  toolActivity: AskNexusToolActivity[];
  /** The answer as text, without card directives. */
  answer: string | null;
  /** The answer in order. Older servers send none; the panel then shows `answer`. */
  blocks?: AskNexusAnswerBlock[];
  /** The choices this message sent from the previous answer's clarification card; null or absent for a typed one. */
  clarificationAnswers?: AskNexusClarificationAnswer[] | null;
  errors: string[];
  status: AskNexusTurnStatus;
}

export interface AskNexusConversation {
  conversationId: string;
  title: string;
  status: AskNexusConversationStatus;
  turns: AskNexusTurn[];
  /** Earlier turns were left out: the conversation is longer than one read. */
  isHistoryTruncated: boolean;
  retryAfterMs?: number;
  /** Present while running: the running answer's id, so the Stop sent on close names that answer and no newer one. */
  runningMessageId?: string | null;
}

export interface AskNexusConversationSummary {
  conversationId: string;
  title: string;
  status: AskNexusConversationStatus;
  createdAt: string;
  updatedAt: string;
}

/** Thrown when the browser has no Firebase sign-in to exchange for the MCP credential. */
export class AskNexusSignInRequiredError extends Error {
  override readonly name = "AskNexusSignInRequiredError";
}

/** Whether this environment runs Ask Nexus at all; the caller's own grants are a separate check. */
export async function fetchAskNexusAvailability(): Promise<{ isAvailable: boolean }> {
  const { data } = await authFetch<{ isAvailable: boolean }>(ASK_NEXUS_AVAILABILITY_PATH);
  return data;
}

export async function fetchAskNexusConversations(): Promise<AskNexusConversationSummary[]> {
  const { data } = await authFetch<AskNexusConversationSummary[]>(ASK_NEXUS_BASE_PATH);
  return data;
}

export async function fetchAskNexusConversation(conversationId: string): Promise<AskNexusConversation> {
  const { data } = await authFetch<AskNexusConversation>(`${ASK_NEXUS_BASE_PATH}/${encodeURIComponent(conversationId)}`);
  return data;
}

/** Where a sent message went: its conversation, and the answer it started, so that answer alone can be stopped. */
export interface AskNexusSentMessage {
  conversationId: string;
  messageId: string | null;
}

/** Starts a conversation, or continues `conversationId`. The server checks it belongs to the caller. */
export async function sendAskNexusMessage(input: {
  message: string;
  conversationId?: string;
  answers?: AskNexusClarificationAnswer[];
}): Promise<AskNexusSentMessage> {
  // Loaded on send: importing it initializes Firebase Auth, which the query barrel must not do.
  const { auth } = await import("@/lib/firebase");
  const firebaseIdToken = await auth.currentUser?.getIdToken();
  if (!firebaseIdToken) throw new AskNexusSignInRequiredError("Sign in again to use Ask Nexus");
  const { data } = await authFetch<{ conversationId: string; messageId?: string | null }>(ASK_NEXUS_BASE_PATH, {
    method: "POST",
    body: JSON.stringify({ ...input, firebaseIdToken, idempotencyKey: crypto.randomUUID() }),
  });
  return { conversationId: data.conversationId, messageId: data.messageId ?? null };
}

/**
 * A 409 means the answer already ended, so there is nothing left to stop. With `messageId`, Nexus stops only that
 * answer, never a newer one.
 */
export async function stopAskNexusAnswer(conversationId: string, messageId?: string): Promise<void> {
  try {
    await authFetch(`${ASK_NEXUS_BASE_PATH}/${encodeURIComponent(conversationId)}/stop`, {
      method: "POST",
      body: JSON.stringify(messageId ? { messageId } : {}),
    });
  } catch (error) {
    if (error instanceof ApiError && error.status === HTTP_CONFLICT) return;
    throw error;
  }
}

function reportPath(conversationId: string, reportId?: string): string {
  const base = `${ASK_NEXUS_BASE_PATH}/${encodeURIComponent(conversationId)}/reports`;
  return reportId ? `${base}/${encodeURIComponent(reportId)}` : base;
}

/** Starts the report a request card names. Starting the same request again returns the same report. */
export async function startAskNexusReport(conversationId: string, requestId: string): Promise<AskNexusReport> {
  const { auth } = await import("@/lib/firebase");
  const firebaseIdToken = await auth.currentUser?.getIdToken();
  if (!firebaseIdToken) throw new AskNexusSignInRequiredError("Sign in again to generate a report");
  const { data } = await authFetch<AskNexusReport>(reportPath(conversationId), {
    method: "POST",
    body: JSON.stringify({ requestId, firebaseIdToken }),
  });
  return data;
}

/**
 * Sent when the panel closes: Nexus cancels the caller's reports for these requests, stops their agent runs and
 * removes their sessions and vaults. Stopping the polling alone would leave them running. Naming the requests keeps
 * a late cancel from touching a report started after the panel reopened.
 */
export async function cancelAskNexusReports(conversationId: string, requestIds: string[]): Promise<void> {
  await authFetch(`${reportPath(conversationId)}/cancel`, { method: "POST", body: JSON.stringify({ requestIds }) });
}

export async function fetchAskNexusReport(conversationId: string, reportId: string): Promise<AskNexusReport> {
  const { data } = await authFetch<AskNexusReport>(reportPath(conversationId, reportId));
  return data;
}

const FILENAME_IN_DISPOSITION = /filename="([^"]+)"/;
const FALLBACK_REPORT_FILENAME = "nexus-report.pptx";

/** The PowerPoint of a ready report. The server checks access again on every download. */
export async function downloadAskNexusReport(conversationId: string, reportId: string): Promise<{ blob: Blob; filename: string }> {
  const response = await authFetch(`${reportPath(conversationId, reportId)}/powerpoint`, { rawResponse: true });
  const filename = FILENAME_IN_DISPOSITION.exec(response.headers.get("content-disposition") ?? "")?.[1] ?? FALLBACK_REPORT_FILENAME;
  return { blob: await response.blob(), filename };
}
